import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// ============================================
// MAIN STATE
// ============================================

export interface ClientDetailsState {
    // Meta
    clientId: string | null
    isLoading: boolean

    // Client Data
    clientProfile: ClientProfileDetails | null
    profileRefetchTrigger: number
    meta: {
        timezones: Timezone[] | undefined;
        countries: Country[] | undefined;
        companySizes: ClientCompanySize[] | undefined;
        clientValues: ClientValueRow[] | undefined;
        clientTypes: PositionClientTypeDropdownRow[] | undefined;
        difficultyLevels: PositionDifficultyLevel[] | undefined;
        employmentTypes: PositionEmploymentType[] | undefined;
        compensationPeriods: PositionCompensationPeriod[] | undefined;
        workplaces: PositionWorkplace[] | undefined;
        scheduleTypes: PositionScheduleType[] | undefined;
        hireTenures: PositionHireTenure[] | undefined;
        emailTemplates: EmailTemplateRow[] | undefined;
        currencies: Currency[] | undefined;
        industries: ClientAvailableJobFieldsDropdownRow[] | undefined;
    } | null
}

// ============================================
// INITIAL STATE
// ============================================

const initialState: ClientDetailsState = {
    clientId: null,
    isLoading: false,
    clientProfile: null,
    profileRefetchTrigger: 0,
    meta: null
}

// ============================================
// SLICE
// ============================================

const clientDetailsSlice = createSlice({
    name: 'clientDetails',
    initialState,
    reducers: {
        // Meta
        setClientId: (state, action: PayloadAction<string | null>) => {
            state.clientId = action.payload
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload
        },
        resetClientDetails: () => initialState,

        // Client Profile
        setClientProfile: (state, action: PayloadAction<ClientProfileDetails>) => {
            state.clientProfile = action.payload
        },
        updateClientProfile: (state, action: PayloadAction<Partial<ClientProfileDetails>>) => {
            if (state.clientProfile) {
                state.clientProfile = { ...state.clientProfile, ...action.payload }
            }
        },
        triggerProfileRefetch: (state) => {
            state.profileRefetchTrigger += 1
        },

        // Meta
        setMeta: (state, action: PayloadAction<ClientDetailsState['meta']>) => {
            state.meta = action.payload
        },
    },
})

// Export actions
export const {
    // Meta
    setClientId,
    setIsLoading,
    resetClientDetails,

    // Client Profile
    setClientProfile,
    updateClientProfile,
    triggerProfileRefetch,

    // Meta
    setMeta,
} = clientDetailsSlice.actions

// Export reducer
export default clientDetailsSlice.reducer
